import { z } from 'zod';
import { parseTagDsl } from './tag-dsl';

/**
 * Per-user named saved searches for `pending-tag-search-saved-views`.
 * Backed by migration 085 (`saved_views`). One row per
 * `(org_id, user_id, entity_kind, name)`.
 *
 * The DSL itself is parsed identically on both sides — the
 * frontend validates the user's input before submit, and the
 * server re-parses on every list query (so a contract bump that
 * tightens the grammar can't silently swallow rows saved against
 * an older parser).
 *
 * v1 supports three entity kinds: `flow`, `run`, and `suite`. The
 * route layer chooses which SQL table to query based on the kind;
 * the DSL grammar is the same across all three (which fields are
 * filterable differs per kind).
 */

export const ENTITY_KINDS = ['flow', 'run', 'suite'] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];
export const EntityKindSchema = z.enum(ENTITY_KINDS);

/**
 * DSL string validator. We accept the raw string (so unknown
 * future grammar keywords don't break old saved views) but
 * `refine` it through the parser to surface obvious syntax
 * errors at write time — a "this DSL won't parse anywhere"
 * row is a strict regression for the user.
 */
export const QueryDslSchema = z
  .string()
  .min(1)
  .max(512)
  .refine(
    (dsl) => {
      const result = parseTagDsl(dsl);
      return result.ok;
    },
    {
      message: 'Could not parse the search DSL. See the inline help (?) for examples.',
    },
  );

export const SavedViewSchema = z.object({
  id: z.string().uuid(),
  org_id: z.string().uuid(),
  user_id: z.string().uuid(),
  entity_kind: EntityKindSchema,
  name: z.string().min(1).max(64),
  query_dsl: QueryDslSchema,
  is_org_default: z.boolean(),
  display_order: z.number().int().nonnegative().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SavedView = z.infer<typeof SavedViewSchema>;

export const CreateSavedViewRequestSchema = z.object({
  entity_kind: EntityKindSchema,
  name: z.string().min(1).max(64),
  query_dsl: QueryDslSchema,
  /**
   * Only org admins can flip this; the route layer enforces. The
   * client may still set the flag (cheaper than a separate
   * endpoint), so the contract accepts it.
   */
  is_org_default: z.boolean().optional(),
  display_order: z.number().int().nonnegative().nullable().optional(),
});
export type CreateSavedViewRequest = z.infer<typeof CreateSavedViewRequestSchema>;

export const UpdateSavedViewRequestSchema = z.object({
  name: z.string().min(1).max(64).optional(),
  query_dsl: QueryDslSchema.optional(),
  is_org_default: z.boolean().optional(),
  display_order: z.number().int().nonnegative().nullable().optional(),
});
export type UpdateSavedViewRequest = z.infer<typeof UpdateSavedViewRequestSchema>;

export const ListSavedViewsResponseSchema = z.object({
  views: z.array(SavedViewSchema),
});
export type ListSavedViewsResponse = z.infer<typeof ListSavedViewsResponseSchema>;
